/**
 * Cart Recommendations Component
 * Manages custom upsell/cross-sell suggestions in the cart drawer.
 */

class CartRecommendations extends HTMLElement {
  constructor() {
    super();
    this.recommendations = [
      {
        variantId: "48433477681460",
        handle: "styling-cream-narte-to-go",
        name: "Styling Cream To-Go",
        image: "https://www.nartecosmeticos.com.br/cdn/shop/files/5_1_2189533f-60df-4fe5-8496-274aa86f2a3b.jpg",
        price: "R$ 44,90"
      },
      {
        variantId: "48306417697076",
        handle: "styling-cream-narte",
        name: "Styling Cream",
        image: "https://cdn.shopify.com/s/files/1/0822/1369/9892/files/styling-cream.jpg",
        price: "R$ 89,90"
      }
    ];

    this._onCartUpdate = this._handleCartUpdate.bind(this);
    this._isAdding = false;
  }

  connectedCallback() {
    document.addEventListener('cart:update', this._onCartUpdate);
    this._render();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this._onCartUpdate);
  }

  _handleCartUpdate(event) {
    const cart = event.detail?.resource || event.detail?.cart || event.detail?.data?.cart;
    if (cart) {
      this._render(cart);
    } else {
      this._fetchAndRender();
    }
  }

  async _fetchAndRender() {
    try {
      const res = await fetch('/cart.js?v=' + Date.now());
      const cart = await res.json();
      this._render(cart);
    } catch (e) {
      console.error('Error fetching cart for recommendations:', e);
    }
  }

  async _render(cartData) {
    let cart = cartData;
    if (!cart) {
      const res = await fetch('/cart.js?v=' + Date.now());
      cart = await res.json();
    }

    const itemsInCart = cart.items.map(item => item.variant_id.toString());
    const toSuggest = this.recommendations.filter(rec => !itemsInCart.includes(rec.variantId));

    if (toSuggest.length === 0) {
      this.innerHTML = '';
      this.style.display = 'none';
      return;
    }

    this.style.display = 'block';
    this.innerHTML = `
      <style>
        .cart-recommendations {
          padding: 15px 0;
          margin-top: 10px;
          border-top: 1px solid #eee;
        }
        .cart-recommendations__title {
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 12px;
          color: #333;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .cart-recommendations__list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .cart-recommendations__item {
          display: flex;
          align-items: center;
          gap: 12px;
          background: #fff;
          padding: 10px;
          border-radius: 8px;
          border: 1px solid #f0f0f0;
          transition: all 0.2s ease;
        }
        .cart-recommendations__item:hover {
          border-color: #a84b1f;
        }
        .cart-recommendations__image {
          width: 60px;
          height: 60px;
          object-fit: cover;
          border-radius: 4px;
        }
        .cart-recommendations__info {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .cart-recommendations__name {
          font-size: 13px;
          font-weight: 500;
          color: #333;
          line-height: 1.2;
        }
        .cart-recommendations__price {
          font-size: 12px;
          color: #a84b1f;
          font-weight: 600;
        }
        .cart-recommendations__add {
          background: #a84b1f;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
          white-space: nowrap;
        }
        .cart-recommendations__add:hover {
          background: #8e3f1a;
        }
        .cart-recommendations__add:disabled {
          background: #ccc;
          cursor: not-allowed;
        }
      </style>
      <div class="cart-recommendations">
        <h3 class="cart-recommendations__title">Sugestões para você</h3>
        <div class="cart-recommendations__list">
          ${toSuggest.map(product => `
            <div class="cart-recommendations__item">
              <img src="${product.image}" alt="${product.name}" class="cart-recommendations__image" loading="lazy">
              <div class="cart-recommendations__info">
                <span class="cart-recommendations__name">${product.name}</span>
                <span class="cart-recommendations__price">${product.price}</span>
              </div>
              <button class="cart-recommendations__add" data-variant-id="${product.variantId}">
                ADICIONAR
              </button>
            </div>
          `).join('')}
        </div>
      </div>
    `;

    this.querySelectorAll('.cart-recommendations__add').forEach(btn => {
      btn.addEventListener('click', (e) => {
        this._addToCart(e.target.dataset.variantId, e.target);
      });
    });
  }

  async _addToCart(variantId, button) {
    if (this._isAdding) return;
    this._isAdding = true;
    button.disabled = true;
    button.innerText = 'ADICIONANDO...';

    try {
      const sections = this._getSectionsToUpdate();
      const body = {
        id: variantId,
        quantity: 1,
        sections: sections.join(','),
        sections_url: window.location.pathname
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      this._refreshUI(data, sections);
    } catch (e) {
      console.error('Error adding recommendation to cart:', e);
      button.disabled = false;
      button.innerText = 'ADICIONAR';
    } finally {
      this._isAdding = false;
    }
  }

  _getSectionsToUpdate() {
    const sections = new Set();
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sections.add(item.dataset.sectionId);
      }
    });
    
    const headerSection = document.querySelector('[id*="header_section"]');
    if (headerSection && headerSection.id) {
        sections.add(headerSection.id.replace('shopify-section-', ''));
    }

    return Array.from(sections);
  }

  _refreshUI(data, sections) {
    if (!data) return;

    // Dispatch event for other components (like tiered-rewards-bar)
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: data,
        data: {
          sections: data.sections || {},
          source: 'cart-recommendations'
        }
      }
    }));

    // Manual morph fallback
    if (data.sections && window.morphSection) {
      for (const sectionId in data.sections) {
        try {
          window.morphSection(sectionId, data.sections[sectionId]);
        } catch (e) {
          console.warn(`Could not morph section ${sectionId}:`, e);
        }
      }
    }
  }
}

if (!window.customElements.get('cart-recommendations')) {
  window.customElements.define('cart-recommendations', CartRecommendations);
}
